/**
 * LinkButton — a router `<Link>` that LOOKS like a Button.
 *
 * Exists because `<Button><Link/></Button>` renders an `<a>` inside a
 * `<button>`, which is invalid HTML: the nesting is undefined behaviour in the
 * parser and screen readers announce it inconsistently. When the action is
 * "go somewhere", the element must be an anchor.
 *
 * Used by: 403 and 404 screens, and the cross-links on the placeholder screens.
 */
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cn, DISABLED, FOCUS_RING } from '@sonauto/ui';

export interface LinkButtonProps {
  readonly to: string;
  readonly variant?: 'primary' | 'secondary';
  readonly className?: string;
  readonly children: ReactNode;
}

export function LinkButton({
  to,
  variant = 'secondary',
  className,
  children,
}: LinkButtonProps): React.JSX.Element {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex min-h-touch select-none items-center justify-center gap-2 rounded-md px-4 text-sm font-medium transition-colors',
        variant === 'primary'
          ? 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-sm'
          : 'bg-surface text-fg ring-1 ring-inset ring-line-strong hover:bg-surface-alt active:bg-surface-sunken',
        FOCUS_RING,
        DISABLED,
        className,
      )}
    >
      {children}
    </Link>
  );
}
